/** Teklif onay akışı — backend ORDER_APPROVAL_WORKFLOW ile uyumlu */
import { humanizeSlug, resolveDisplayStepLabel } from "./workflowDisplay";
export const ORDER_APPROVAL_WORKFLOW = [
  {
    stepNumber: 1,
    label: "Yönetici Kontrol",
    description: "Teklif doğru mu? Onay → kullanıcıya gider.",
    actor: "admin",
  },
  {
    stepNumber: 2,
    label: "Müşteriye Sunum",
    description:
      "Teklif müşteriye sunulur; onay = müşteri kabulü → sipariş onayına gider.",
    actor: "user",
  },
  {
    stepNumber: 3,
    label: " Sipariş Onayı",
    description: "Yönetici siparişa izin verir.",
    actor: "admin",
  },
  {
    stepNumber: 4,
    label: "Sipariş Oluşturma",
    description: "Kullanıcı teklifi siparişa dönüştürür.",
    actor: "user",
  },
];

export function resolveOrderApprovalStepMeta(item) {
  const resolvedLabel = resolveDisplayStepLabel(item);
  // Workflow-based: if item has stepLabel from ApprovalRecord, use it directly
  if (resolvedLabel) {
    return {
      stepNumber: item.stepNumber,
      stepLabel: resolvedLabel,
      stepDescription: item.stepDescription,
      approveActionLabel: item.approveActionLabel || "Onayla",
      approveModalTitle: item.approveModalTitle,
      approveModalMessage: item.approveModalMessage,
      rejectActionLabel: item.rejectActionLabel || "Reddet",
      rejectModalTitle: item.rejectModalTitle || "Reddet",
      rejectModalMessage: item.rejectModalMessage,
      onRejectStatus: item.onRejectStatus,
    };
  }

  const stage = item?.stage;
  const approvalStep = item?.approvalStep;
  const orderStatus = item?.entity?.status;

  if (stage === "user") {
    return {
      stepNumber: 2,
      stepLabel: "Müşteriye Sunum",
      stepDescription:
        "Teklifi müşteriye sunun. Onay, müşterinin teklifi kabul ettiğini belirtir; ardından yönetici sipariş onayına gider.",
      approveActionLabel: "Sipariş Onayı Bekliyor",
      approveModalTitle: undefined,
      approveModalMessage: undefined,
      rejectActionLabel: "Reddet",
      rejectModalTitle: "Teklifi Reddet",
      rejectModalMessage: undefined,
      onRejectStatus: undefined,
    };
  }

  if (approvalStep === 2 || orderStatus === "user_approved") {
    return {
      stepNumber: 3,
      stepLabel: "Sipariş Onayı",
      stepDescription:
        "Müşteri onayından geçen teklifi siparişa dönüştürmeye izin verin. Onay sonrası kullanıcı Siparişi oluşturur (Adım 4).",
      approveActionLabel: "Siparişi Onayla",
      approveModalTitle: undefined,
      approveModalMessage: undefined,
      rejectActionLabel: "Reddet",
      rejectModalTitle: "Teklifi Reddet",
      rejectModalMessage: undefined,
      onRejectStatus: undefined,
    };
  }

  return {
    stepNumber: 1,
    stepLabel: "Yönetici Kontrol Onayı",
    stepDescription:
      "Yeni teklifin içeriğini kontrol edin. Onay sonrası teklif müşteriye sunulur (Adım 2).",
    approveActionLabel: "Onayla",
    approveModalTitle: undefined,
    approveModalMessage: undefined,
    rejectActionLabel: "Reddet",
    rejectModalTitle: "Teklifi Reddet",
    rejectModalMessage: undefined,
    onRejectStatus: undefined,
  };
}

export function getItemApprovalLabels(item, stage = "admin") {
  if (item?.type === "order" || item?.entityType === "order") {
    return resolveOrderApprovalStepMeta(item);
  }

  // Sale (and any other workflow entity): use ApprovalRecord step labels
  const resolvedLabel = resolveDisplayStepLabel(item);
  if (resolvedLabel) {
    return {
      stepNumber: item?.stepNumber ?? item?.approvalStep ?? null,
      stepLabel: resolvedLabel,
      stepDescription: item?.stepDescription || null,
      approveActionLabel: item?.approveActionLabel || "Onayla",
      approveModalTitle: item?.approveModalTitle,
      approveModalMessage: item?.approveModalMessage,
      rejectActionLabel: item?.rejectActionLabel || "Reddet",
      rejectModalTitle: item?.rejectModalTitle || "Reddet",
      rejectModalMessage: item?.rejectModalMessage,
      onRejectStatus: item?.onRejectStatus,
    };
  }

  return {
    stepNumber: null,
    stepLabel: null,
    stepDescription: null,
    approveActionLabel: null,
    approveModalTitle: null,
    approveModalMessage: null,
    rejectActionLabel: null,
    rejectModalTitle: null,
    rejectModalMessage: null,
    onRejectStatus: null,
  };
}

/** Sonuç / durum sütunu ve detay için adım bazlı rozet metni */
export function getApprovalResultBadge(item) {
  const meta = getItemApprovalLabels(item);

  const orderStatus = item?.entity?.status;

  if (!meta?.stepLabel) {
    if (item?.status === "approved") {
      return {
        label: "Onaylandı",
        appearance: "success-subtle",
        title: undefined,
      };
    }
    if (item?.status === "rejected" || orderStatus === "rejected") {
      // Use onRejectStatus or rejectModalTitle for label if available
      let label = "Reddedildi";
      if (item?.onRejectStatus && item.onRejectStatus !== "rejected") {
        label = item.onRejectStatus
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
      } else if (item?.rejectModalTitle && item.rejectModalTitle !== "Reddet") {
        label = item.rejectModalTitle;
      }
      return {
        label,
        appearance: "danger-subtle",
        title: item?.reason,
      };
    }
    if (orderStatus === "cancelled") {
      return {
        label: "İptal",
        appearance: "neutral-subtle",
        title: undefined,
      };
    }
    return {
      label: "Bekliyor",
      appearance: "warning-subtle",
      title: undefined,
    };
  }

  const stepAppearance =
    meta.stepNumber === 1
      ? "warning-subtle"
      : meta.stepNumber === 2
        ? "info-subtle"
        : "primary-subtle";

  if (item?.status === "rejected" || orderStatus === "rejected") {
    let label = "Reddedildi";
    if (item?.onRejectStatus && item.onRejectStatus !== "rejected") {
      label = item.onRejectStatus
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    } else if (meta?.rejectModalTitle && meta.rejectModalTitle !== "Reddet") {
      label = meta.rejectModalTitle;
    }
    return {
      label: `${meta.stepLabel} — ${label}`,
      appearance: "danger-subtle",
      title: item?.reason || "Teklif reddedildi.",
    };
  }

  if (orderStatus === "cancelled") {
    return {
      label: `${meta.stepLabel} — İptal Edildi`,
      appearance: "neutral-subtle",
      title: "Teklif iptal edildi.",
    };
  }

  if (item?.status === "pending") {
    return {
      label: meta.stepLabel,
      appearance: stepAppearance,
      title: `${meta.stepDescription}\n\nDurum: Onay bekliyor`,
    };
  }

  if (item?.status === "approved") {
    const label = meta.stepLabel || "Onaylandı";
    const alreadyMarked = /onayland/i.test(label);
    return {
      label: alreadyMarked ? label : `${label} — Onaylandı`,
      appearance: "success-subtle",
      title: meta.stepDescription,
    };
  }

  return {
    label: meta.stepLabel,
    appearance: stepAppearance,
    title: meta.stepDescription,
  };
}
